import { Cartesian2, Math as MathCesium, Cartographic, Color, defined, HorizontalOrigin, LabelStyle, ScreenSpaceEventHandler, ScreenSpaceEventType, VerticalOrigin, CallbackProperty } from "cesium";
import "../../styles/flycamera.css"
import { getCode } from "../../class/epsgUtils";
import loadingRollImg from "../../assets/images/loading-roll.png"
import { CesiumSnap } from "./cesiumSnap";
import { showStatusStack } from "../../utils/utils";
import { getMessage } from "../../language";

export class Coordinates {
    viewer: any;
    handler: any
    containerId: string;
    cesiumSnap: CesiumSnap;
    private _statusStackItem?: { remove: () => void };

    constructor(containerId: string, viewer: any) {
        this.containerId = containerId;
        this.viewer = viewer;
        this.cesiumSnap = new CesiumSnap(viewer);
    }

    entityIdCounterPosition: number = 0;
    activeCreatePointXYZ: boolean = false;
    activePointXYZEntities: any = {}
    lastPoint: any = null;

    getWGS84ToVN2000 = async (epsg: any, longitude: any, latitude: any, height: any) => {
        try {
            const apiUrl_GetWGS84ToVN2000 = `https://geotrans.corebim.com/CoordinateConverter/GetWGS84ToVN2000?epsg=${epsg}&lat=${latitude}&lon=${longitude}&h=${height}`;
            const response = await fetch(apiUrl_GetWGS84ToVN2000, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching the data:', error);
        }
    }

    createPointXYZCesium = (epsg: any) => {
        this.removeAllEvents();

        if (!this._statusStackItem) {
            this._statusStackItem = showStatusStack(this.containerId, getMessage("coordinate-state"));
        }

        this.cesiumSnap.enable();

        this.handler = new ScreenSpaceEventHandler(this.viewer.canvas);

        this.handler.setInputAction(async (event: any) => {
            var cartesian = (this.cesiumSnap.isSnapped && this.cesiumSnap.lastSnappedPosition)
                ? this.cesiumSnap.lastSnappedPosition
                : this.cesiumSnap.getSnappedPosition(event.position);

            if (cartesian) {
                var pickedObject = this.viewer.scene.drillPick(event.position);

                if (pickedObject.length == 0 && !this.cesiumSnap.isSnapped) {
                    var ray = this.viewer.camera.getPickRay(event.position);
                    cartesian = this.viewer.scene.globe.pick(ray!, this.viewer.scene);
                }

                if (!cartesian) return;

                let flagPickNearPoint = pickedObject.some((element: any) =>
                    element?.id?.id.includes("position-cesium-")
                );

                if (flagPickNearPoint) return;

                var cartographic = Cartographic.fromCartesian(cartesian);
                var longitude = MathCesium.toDegrees(cartographic.longitude);
                var latitude = MathCesium.toDegrees(cartographic.latitude);
                var height = cartographic.height;

                const epsgChoose = getCode(longitude, latitude)
                
                let loadingRoll = this.viewer.entities.add({
                    position: cartesian,
                    billboard: {
                        image: loadingRollImg,
                        rotation: new CallbackProperty(function (time, result) {
                            return performance.now() / 300;
                        }, false),
                        disableDepthTestDistance: Number.POSITIVE_INFINITY,
                        width: 15,
                        height: 15,
                    }
                })

                let pointVN2000 = await this.getWGS84ToVN2000(epsgChoose.Code, longitude, latitude, height);

                if (defined(cartesian)) {
                    var entityIdBase = 'position-cesium-' + this.entityIdCounterPosition++;
                    var x = Number(pointVN2000[0]);
                    var y = Number(pointVN2000[1]);
                    var z = Number(pointVN2000[2]);

                    if (
                        this.lastPoint &&
                        Math.abs(this.lastPoint.x - x) < 0.001 &&
                        Math.abs(this.lastPoint.y - y) < 0.001 &&
                        Math.abs(this.lastPoint.z - z) < 0.001
                    ) {
                        console.log("Điểm trùng với điểm cuối cùng, bỏ qua.");
                        return;
                    }

                    this.lastPoint = { x, y, z };

                    let formattedCoords = this.formatCoordinatesXYZ(x, y, z);

                    let point = this.viewer.entities.add({
                        id: entityIdBase,
                        position: cartesian,
                        point: {
                            color: Color.WHITE,
                            pixelSize: 6,
                            outlineColor: Color.BLUE,
                            outlineWidth: 2,
                            disableDepthTestDistance: Number.POSITIVE_INFINITY,
                        },
                    });

                    const font = '10pt monospace';
                    const fillColor = Color.WHITE;
                    const backgroundPadding = new Cartesian2(5, 5);
                    const disableDepthTestDistance = Number.POSITIVE_INFINITY;

                    let labels = [];

                    labels.push(this.viewer.entities.add({
                        id: entityIdBase + '-label-x-symbol',
                        position: cartesian,
                        label: {
                            text: `X`,
                            font: font,
                            fillColor: fillColor,
                            showBackground: true,
                            backgroundColor: Color.fromCssColorString('#f12c2c'),
                            backgroundPadding: backgroundPadding,
                            pixelOffset: (pickedObject.length == 0) ? new Cartesian2(7, -30) : new Cartesian2(7, -50),
                            labelStyle: LabelStyle.FILL,
                            verticalOrigin: VerticalOrigin.BOTTOM,
                            horizontalOrigin: HorizontalOrigin.LEFT,
                            disableDepthTestDistance: disableDepthTestDistance,
                        }
                    }));

                    labels.push(this.viewer.entities.add({
                        id: entityIdBase + '-label-x-value',
                        position: cartesian,
                        label: {
                            text: `${formattedCoords.x}`,
                            font: font,
                            fillColor: fillColor,
                            showBackground: true,
                            backgroundColor: new Color(0.0, 0.0, 0.0, 0.5),
                            backgroundPadding: backgroundPadding,
                            pixelOffset: (pickedObject.length == 0) ? new Cartesian2(25, -30) : new Cartesian2(25, -50),
                            labelStyle: LabelStyle.FILL,
                            verticalOrigin: VerticalOrigin.BOTTOM,
                            horizontalOrigin: HorizontalOrigin.LEFT,
                            disableDepthTestDistance: disableDepthTestDistance,
                        }
                    }));

                    labels.push(this.viewer.entities.add({
                        id: entityIdBase + '-label-y-symbol',
                        position: cartesian,
                        label: {
                            text: `Y`,
                            font: font,
                            fillColor: fillColor,
                            showBackground: true,
                            backgroundColor: Color.fromCssColorString('#0bb80b'),
                            backgroundPadding: backgroundPadding,
                            pixelOffset: (pickedObject.length == 0) ? new Cartesian2(7, -10) : new Cartesian2(7, -30),
                            labelStyle: LabelStyle.FILL,
                            verticalOrigin: VerticalOrigin.BOTTOM,
                            horizontalOrigin: HorizontalOrigin.LEFT,
                            disableDepthTestDistance: disableDepthTestDistance,
                        }
                    }));

                    labels.push(this.viewer.entities.add({
                        id: entityIdBase + '-label-y-value',
                        position: cartesian,
                        label: {
                            text: `${formattedCoords.y}`,
                            font: font,
                            fillColor: fillColor,
                            showBackground: true,
                            backgroundColor: new Color(0.0, 0.0, 0.0, 0.5),
                            backgroundPadding: backgroundPadding,
                            pixelOffset: (pickedObject.length == 0) ? new Cartesian2(25, -10) : new Cartesian2(25, -30),
                            labelStyle: LabelStyle.FILL,
                            verticalOrigin: VerticalOrigin.BOTTOM,
                            horizontalOrigin: HorizontalOrigin.LEFT,
                            disableDepthTestDistance: disableDepthTestDistance,
                        }
                    }));

                    if (pickedObject.length > 0) {
                        labels.push(this.viewer.entities.add({
                            id: entityIdBase + '-label-z-symbol',
                            position: cartesian,
                            label: {
                                text: `Z`,
                                font: font,
                                fillColor: fillColor,
                                showBackground: true,
                                backgroundColor: Color.fromCssColorString('#2c2cf1'),
                                backgroundPadding: backgroundPadding,
                                pixelOffset: new Cartesian2(7, -10),
                                labelStyle: LabelStyle.FILL,
                                verticalOrigin: VerticalOrigin.BOTTOM,
                                horizontalOrigin: HorizontalOrigin.LEFT,
                                disableDepthTestDistance: disableDepthTestDistance,
                            }
                        }));

                        labels.push(this.viewer.entities.add({
                            id: entityIdBase + '-label-z-value',
                            position: cartesian,
                            label: {
                                text: `${formattedCoords.z}`,
                                font: font,
                                fillColor: fillColor,
                                showBackground: true,
                                backgroundColor: new Color(0.0, 0.0, 0.0, 0.5),
                                backgroundPadding: backgroundPadding,
                                pixelOffset: new Cartesian2(25, -10),
                                labelStyle: LabelStyle.FILL,
                                verticalOrigin: VerticalOrigin.BOTTOM,
                                horizontalOrigin: HorizontalOrigin.LEFT,
                                disableDepthTestDistance: disableDepthTestDistance,
                            }
                        }));
                    }

                    this.activePointXYZEntities[entityIdBase] = {
                        point: point,
                        labels: labels
                    };
                    if (loadingRoll) {
                        this.viewer.entities.remove(loadingRoll)
                    }
                } else {
                    console.log("Không thể pick vị trí trên bề mặt hoặc vật thể.");
                }
            }

            this.viewer.scene.requestRender();

        }, ScreenSpaceEventType.LEFT_CLICK);

        this.handler.setInputAction((move: ScreenSpaceEventHandler.MotionEvent) => {
            this.cesiumSnap.updateSnapThrottled(move.endPosition, () => {
                this.viewer.scene.requestRender();
            });
        }, ScreenSpaceEventType.MOUSE_MOVE);
    }

    removePointXYZCesium = () => {
        this.removeAllEvents();

        this.handler = new ScreenSpaceEventHandler(this.viewer.canvas);

        this.handler.setInputAction((event: any) => {
            const pickedEntity = this.viewer.scene.pick(event.position);
            if (defined(pickedEntity) && defined(pickedEntity.id) && pickedEntity.id.id.includes("position-cesium-")) {
                const fullEntityId = pickedEntity.id.id;
                const entityId = fullEntityId.split('-').slice(0, 3).join('-');

                const pointData = this.activePointXYZEntities[entityId];

                if (pointData) {
                    this.viewer.entities.remove(pointData.point);
                    pointData.labels.forEach((label: any, index: number) => {
                        this.viewer.entities.remove(label);
                    });
                    delete this.activePointXYZEntities[entityId];
                }
            }
            this.viewer.scene.requestRender();
        }, ScreenSpaceEventType.LEFT_CLICK)
    }

    removeAllPointXYZs = () => {
        for (var i = 0; i < this.entityIdCounterPosition; i++) {
            if (this.activePointXYZEntities[`position-cesium-${i}`]) {
                this.viewer.entities.remove(this.activePointXYZEntities[`position-cesium-${i}`].point);
                this.activePointXYZEntities[`position-cesium-${i}`].labels.forEach((label: any, index: number) => {
                    this.viewer.entities.remove(label);
                });
            }
        }
        this.entityIdCounterPosition = 0;
        this.activePointXYZEntities = {};
        this.removeAllEvents();
        this.viewer.scene.requestRender();
    }

    showHideAllPointXYZs = (show: boolean) => {
        for (var i = 0; i < this.entityIdCounterPosition; i++) {
            if (this.activePointXYZEntities[`position-cesium-${i}`]) {
                this.activePointXYZEntities[`position-cesium-${i}`].point.show = show
                this.activePointXYZEntities[`position-cesium-${i}`].labels.forEach((label: any, index: number) => {
                    label.show = show
                })
            }
        }
        this.removeAllEvents();
        this.viewer.scene.requestRender();
    }

    formatCoordinatesXYZ = (x: any, y: any, z: any) => {
        let xFormatted = x.toFixed(2);
        let yFormatted = y.toFixed(2);
        let zFormatted = z.toFixed(2);

        let maxLength = Math.max(xFormatted.length, yFormatted.length, zFormatted.length);

        xFormatted = xFormatted.padStart(maxLength, ' ');
        yFormatted = yFormatted.padStart(maxLength, ' ');
        zFormatted = zFormatted.padStart(maxLength, ' ');

        return {
            x: xFormatted,
            y: yFormatted,
            z: zFormatted
        };
    }

    removeAllEvents = () => {
        if (this.handler) {
            this.handler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
            this.handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
            this.handler.removeInputAction(ScreenSpaceEventType.RIGHT_CLICK);
        }
        this.cesiumSnap.disable();
    }

    refreshAllAction = () => {
        this.removeAllEvents();
        if (this._statusStackItem) {
            this._statusStackItem.remove();
            this._statusStackItem = undefined;
        }
    }
}
